import { useState, useEffect, useCallback } from 'react';
import { Button } from '../components/ui/button';
import { apiFetch } from '../fetchInterceptor';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { useWorkspace } from '../context/WorkspaceContext';
import { toast } from 'sonner';
import { 
  Upload, 
  File, 
  FileText, 
  Image, 
  Paperclip,
  Download,
  Trash2,
  X,
  Loader2,
  Eye,
  Link2,
  Unlink
} from 'lucide-react';
import { cn } from '../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// File icon based on mime type
const getFileIcon = (mimeType) => {
  if (mimeType?.startsWith('image/')) return Image;
  if (mimeType?.includes('pdf')) return FileText;
  return File;
};

// Format file size
const formatFileSize = (bytes) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

// Attachment row component
const AttachmentRow = ({ attachment, onDownload, onDelete, onUnlink, linkType, linkId, canDelete }) => {
  const FileIcon = getFileIcon(attachment.mime_type);
  const [downloading, setDownloading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  
  const handleDownload = async () => {
    setDownloading(true);
    try {
      await onDownload(attachment);
    } finally {
      setDownloading(false);
    }
  };
  
  const handleDelete = async () => {
    setDeleting(true);
    try {
      await onDelete(attachment.attachment_id);
    } finally {
      setDeleting(false);
    }
  };
  
  return (
    <TableRow data-testid={`attachment-${attachment.attachment_id}`}>
      <TableCell>
        <div className="flex items-center gap-3">
          <div className="p-2 bg-muted rounded">
            <FileIcon className="w-4 h-4" />
          </div>
          <div>
            <p className="font-medium truncate max-w-[200px]">{attachment.original_filename}</p>
            <p className="text-xs text-muted-foreground">
              {formatFileSize(attachment.size)} · {new Date(attachment.uploaded_at).toLocaleDateString()}
            </p>
          </div>
        </div>
      </TableCell>
      <TableCell className="text-right">
        <div className="flex items-center justify-end gap-1">
          <Button 
            size="sm" 
            variant="ghost"
            onClick={handleDownload}
            disabled={downloading}
            data-testid={`download-${attachment.attachment_id}`}
          >
            {downloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
          </Button>
          {linkType && linkId && (
            <Button
              size="sm"
              variant="ghost"
              onClick={() => onUnlink(attachment.attachment_id)}
              title="Unlink from this item"
            >
              <Unlink className="w-4 h-4" />
            </Button>
          )}
          {canDelete && (
            <Button 
              size="sm" 
              variant="ghost"
              onClick={handleDelete}
              disabled={deleting}
              className="text-destructive hover:text-destructive"
              data-testid={`delete-${attachment.attachment_id}`}
            >
              {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
            </Button>
          )}
        </div>
      </TableCell>
    </TableRow>
  );
};

// Upload dialog component
const UploadDialog = ({ open, onOpenChange, onUpload, linkType, linkId }) => {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  
  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };
  
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      setFile(e.dataTransfer.files[0]);
    }
  };
  
  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setFile(e.target.files[0]);
    }
  };
  
  const handleUpload = async () => {
    if (!file) return;
    
    setUploading(true);
    try {
      await onUpload(file, linkType, linkId);
      setFile(null);
      onOpenChange(false);
    } finally {
      setUploading(false);
    }
  };
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Upload Attachment</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          <div
            className={cn(
              "border-2 border-dashed rounded-lg p-8 text-center transition-colors",
              dragActive ? "border-primary bg-primary/5" : "border-muted-foreground/25",
              file && "border-emerald-500 bg-emerald-500/5"
            )}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
          >
            {file ? (
              <div className="flex items-center justify-center gap-3">
                <File className="w-8 h-8 text-emerald-500" />
                <div className="text-left">
                  <p className="font-medium">{file.name}</p>
                  <p className="text-sm text-muted-foreground">{formatFileSize(file.size)}</p>
                </div>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setFile(null)}
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            ) : (
              <div>
                <Upload className="w-8 h-8 mx-auto mb-2 text-muted-foreground" />
                <p className="text-sm text-muted-foreground mb-2">
                  Drag and drop a file here, or click to browse
                </p>
                <Input
                  type="file"
                  className="hidden"
                  id="file-upload"
                  onChange={handleFileChange}
                  accept=".pdf,.jpg,.jpeg,.png,.gif,.webp,.doc,.docx,.xls,.xlsx,.csv,.txt"
                />
                <Label htmlFor="file-upload" className="cursor-pointer">
                  <Button variant="outline" size="sm" type="button" asChild>
                    <span>Browse Files</span>
                  </Button>
                </Label>
              </div>
            )}
          </div>
          
          <p className="text-xs text-muted-foreground">
            Supported: PDF, Images, Word, Excel, CSV, Text (max 10MB)
          </p>
          
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button onClick={handleUpload} disabled={!file || uploading}>
              {uploading ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Uploading...</>
              ) : (
                <><Upload className="w-4 h-4 mr-2" /> Upload</>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Main Attachments component
export default function Attachments({ 
  linkType, 
  linkId, 
  title = "Attachments",
  canUpload = true,
  canDelete = true,
  compact = false
}) {
  const { currentWorkspace } = useWorkspace();
  const [attachments, setAttachments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadOpen, setUploadOpen] = useState(false);
  
  const fetchAttachments = useCallback(async () => {
    if (!currentWorkspace) return;
    
    try {
      let url = `${API_URL}/api/attachments?workspace_id=${currentWorkspace.workspace_id}`;
      if (linkType && linkId) {
        url += `&link_type=${linkType}&link_id=${linkId}`;
      }
      
      const response = await apiFetch(url);
      
      if (response.ok) {
        const data = await response.json();
        setAttachments(data.filter(a => !a.deleted));
      }
    } catch (error) {
      console.error('Failed to fetch attachments:', error);
    } finally {
      setLoading(false);
    }
  }, [currentWorkspace, linkType, linkId]);
  
  useEffect(() => {
    fetchAttachments();
  }, [fetchAttachments]);
  
  const handleUpload = async (file, lType, lId) => {
    if (!currentWorkspace) return;
    
    const formData = new FormData();
    formData.append('file', file);
    formData.append('workspace_id', currentWorkspace.workspace_id);
    if (lType) formData.append('link_type', lType);
    if (lId) formData.append('link_id', lId);
    
    const response = await apiFetch(`${API_URL}/api/attachments/upload`, {
      method: 'POST',
      body: formData
    });
    
    if (response.ok) {
      toast.success('File uploaded successfully');
      fetchAttachments();
    } else {
      const error = await response.json();
      toast.error(error.detail || 'Upload failed');
      throw new Error(error.detail);
    }
  };
  
  const handleDownload = async (attachment) => {
    try {
      const response = await apiFetch(
        `${API_URL}/api/attachments/${attachment.attachment_id}/download`
      );
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = attachment.original_filename;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        toast.error('Download failed');
      }
    } catch (error) {
      toast.error('Download failed');
    }
  };
  
  const handleDelete = async (attachmentId) => {
    try {
      const response = await apiFetch(
        `${API_URL}/api/attachments/${attachmentId}`,
        { method: 'DELETE' }
      );
      
      if (response.ok) {
        toast.success('Attachment deleted');
        fetchAttachments();
      } else {
        toast.error('Delete failed');
      }
    } catch (error) {
      toast.error('Delete failed');
    }
  };
  
  const handleUnlink = async (attachmentId) => {
    if (!linkType || !linkId) return;
    
    try {
      const response = await apiFetch(
        `${API_URL}/api/attachments/${attachmentId}/unlink`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ link_type: linkType, link_id: linkId })
        }
      );
      
      if (response.ok) {
        toast.success('Attachment unlinked');
        fetchAttachments();
      } else {
        toast.error('Unlink failed');
      }
    } catch (error) {
      toast.error('Unlink failed');
    }
  };
  
  if (compact) {
    return (
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm font-medium">
            <Paperclip className="w-4 h-4" />
            {title} ({attachments.length})
          </div>
          {canUpload && (
            <Button size="sm" variant="outline" onClick={() => setUploadOpen(true)}>
              <Upload className="w-3 h-3 mr-1" /> Add
            </Button>
          )}
        </div>
        
        {loading ? (
          <div className="flex items-center justify-center py-4">
            <Loader2 className="w-4 h-4 animate-spin" />
          </div>
        ) : attachments.length === 0 ? (
          <p className="text-sm text-muted-foreground py-2">No attachments</p>
        ) : (
          <div className="space-y-1">
            {attachments.map(att => {
              const FileIcon = getFileIcon(att.mime_type);
              return (
                <div 
                  key={att.attachment_id}
                  className="flex items-center justify-between p-2 bg-muted/50 rounded text-sm"
                >
                  <div className="flex items-center gap-2 truncate">
                    <FileIcon className="w-4 h-4 flex-shrink-0" />
                    <span className="truncate">{att.original_filename}</span>
                  </div>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-6 w-6 p-0"
                    onClick={() => handleDownload(att)}
                  >
                    <Download className="w-3 h-3" />
                  </Button>
                </div>
              );
            })}
          </div>
        )}
        
        <UploadDialog
          open={uploadOpen}
          onOpenChange={setUploadOpen}
          onUpload={handleUpload}
          linkType={linkType}
          linkId={linkId}
        />
      </div>
    );
  }
  
  return (
    <Card data-testid="attachments-card">
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <CardTitle className="text-lg flex items-center gap-2">
          <Paperclip className="w-5 h-5" />
          {title}
        </CardTitle>
        {canUpload && (
          <Button size="sm" onClick={() => setUploadOpen(true)} data-testid="upload-attachment-btn">
            <Upload className="w-4 h-4 mr-2" /> Upload
          </Button>
        )}
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin" />
          </div>
        ) : attachments.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <Paperclip className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p>No attachments yet</p>
            {canUpload && (
              <p className="text-sm mt-1">Upload receipts, invoices, or other documents</p>
            )}
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>File</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {attachments.map(att => (
                <AttachmentRow
                  key={att.attachment_id}
                  attachment={att}
                  onDownload={handleDownload}
                  onDelete={handleDelete}
                  onUnlink={handleUnlink}
                  linkType={linkType}
                  linkId={linkId}
                  canDelete={canDelete}
                />
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
      
      <UploadDialog
        open={uploadOpen}
        onOpenChange={setUploadOpen}
        onUpload={handleUpload}
        linkType={linkType}
        linkId={linkId}
      />
    </Card>
  );
}
